import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../../services/notification.service';
import { AddButtonComponent } from '../../../../../shared/components/add-button/add-button.component';
import { EmptyResultComponent } from '../../../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { LaneService } from '../../../../services/lane.service';
import { LaneTableRow } from '../../../../models/lane.model';
import { countActiveLanes, mapLaneFromApi } from '../../../../utils/lane.util';
import { LaneFormModalComponent } from './lane-form-modal/lane-form-modal.component';
import { DeleteLaneModalComponent } from './delete-lane-modal/delete-lane-modal.component';

/**
 * Lane management of one location (company detail → Location → Lanes).
 * Moved from the former Location Settings page; emits the active lane count for the tab badge.
 */
@Component({
  selector: 'app-location-lanes',
  standalone: true,
  imports: [
    CommonModule,
    AddButtonComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    LaneFormModalComponent,
    DeleteLaneModalComponent,
  ],
  templateUrl: './location-lanes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-lanes.component.scss'],
})
export class LocationLanesComponent implements OnChanges {
  @Input() companyId = '';
  @Input() locationId = '';
  /** Sub-tab visible; becoming visible reloads so gate connection dots are fresh. */
  @Input() active = true;
  @Output() activeCountChange = new EventEmitter<number>();

  lanes: LaneTableRow[] = [];
  loading = false;
  showLaneFormModal = false;
  laneFormMode: 'create' | 'edit' = 'create';
  editingLaneId = '';
  laneToDelete: LaneTableRow | null = null;
  isDeletingLane = false;
  private deleteLaneContext: { companyId: string; locationId: string } | null = null;

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Lane Name', width: 'md' },
    { label: 'Camera', width: 'sm' },
    { label: 'Gate', width: 'sm' },
    { label: 'Type', width: 'sm' },
    { label: 'Status', width: 'sm' },
  ];

  private readonly laneService = inject(LaneService);
  private readonly messageService = inject(NotificationService);
  /** Bumped per lane load so a slow reply from a superseded reload can be discarded. */
  private laneLoadSeq = 0;

  ngOnChanges(changes: SimpleChanges): void {
    const contextChanged = Boolean(changes['companyId'] || changes['locationId']);
    if (contextChanged) {
      this.resetDeleteLaneModal();
      this.loadLanes();
      return;
    }
    const becameActive =
      changes['active'] && !changes['active'].firstChange && changes['active'].currentValue;
    if (becameActive) {
      this.loadLanes();
    }
  }

  get hasLocationContext(): boolean {
    return Boolean(this.companyId && this.locationId);
  }

  loadLanes(): void {
    if (!this.hasLocationContext) {
      this.laneLoadSeq += 1;
      this.lanes = [];
      this.loading = false;
      return;
    }

    const companyId = this.companyId;
    const locationId = this.locationId;
    const seq = ++this.laneLoadSeq;
    this.loading = true;
    this.lanes = [];

    this.laneService.listLanes(companyId, locationId).subscribe({
      next: response => {
        // A reload of the same location keeps the ids equal; the load token tells a
        // superseded reply apart so it cannot replace newer lanes or the tab count.
        if (seq !== this.laneLoadSeq) {
          return;
        }
        this.loading = false;
        if (response.status && response.data) {
          const laneList = Array.isArray(response.data) ? response.data : [];
          this.lanes = laneList.map(lane => mapLaneFromApi(lane));
          this.activeCountChange.emit(countActiveLanes(this.lanes));
          this.refreshDeviceConnectionStatus(companyId, locationId, seq);
        }
      },
      error: () => {
        if (seq !== this.laneLoadSeq) {
          return;
        }
        this.loading = false;
        this.lanes = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load lanes',
        });
      },
    });
  }

  /**
   * Resolve each camera's and gate's live connection state from the SSE registry.
   *
   * The lane payload carries no connection column (CWS-1262), so the indicators have to
   * ask sse-api. The camera and gate are usually the same Axis device on one SSE stream,
   * so serials are de-duplicated across both columns: one call per distinct serial. A
   * failure leaves that row showing disconnected rather than blocking the table, because
   * admin-panel deliberately errors instead of guessing when sse-api is unreachable.
   */
  private refreshDeviceConnectionStatus(companyId: string, locationId: string, seq: number): void {
    const serials = [
      ...new Set(this.lanes.flatMap(lane => [lane.cameraSerial, lane.gateSerial]).filter(Boolean)),
    ];

    for (const serial of serials as string[]) {
      this.laneService.checkDeviceConnection(serial).subscribe({
        next: response => {
          // Reloading the same location keeps companyId/locationId equal, so identity
          // alone cannot tell a superseded reply from a current one — gate on the load
          // token as well, or a slow earlier reply overwrites a newer result.
          if (
            seq !== this.laneLoadSeq ||
            companyId !== this.companyId ||
            locationId !== this.locationId
          ) {
            return;
          }
          const connected = !!response?.data?.connected;
          this.lanes = this.lanes.map(lane => ({
            ...lane,
            cameraConnected: lane.cameraSerial === serial ? connected : lane.cameraConnected,
            gateConnected: lane.gateSerial === serial ? connected : lane.gateConnected,
          }));
        },
        error: () => {
          // Leave the row disconnected; the console error from LaneService is enough.
        },
      });
    }
  }

  getTypeLabel(type: LaneTableRow['type']): string {
    if (!type) {
      return '—';
    }
    return type === 'automatic' ? 'Automatic' : 'Manual';
  }

  getStatusLabel(status: LaneTableRow['status']): string {
    return status === 'active' ? 'Active' : 'Inactive';
  }

  onAddLane(): void {
    this.laneFormMode = 'create';
    this.editingLaneId = '';
    this.showLaneFormModal = true;
  }

  onCloseLaneFormModal(): void {
    this.showLaneFormModal = false;
    this.editingLaneId = '';
  }

  onLaneSaved(_event: { companyId: string; locationId: string }): void {
    this.loadLanes();
  }

  onEditLane(lane: LaneTableRow): void {
    this.laneFormMode = 'edit';
    this.editingLaneId = lane.id;
    this.showLaneFormModal = true;
  }

  onDeleteLane(lane: LaneTableRow): void {
    this.laneToDelete = lane;
    // Capture the context the lane belongs to so a later location switch
    // can't cause a confirm to delete under the wrong company/location.
    this.deleteLaneContext = {
      companyId: this.companyId,
      locationId: this.locationId,
    };
  }

  onCloseDeleteLaneModal(): void {
    if (!this.isDeletingLane) {
      this.resetDeleteLaneModal();
    }
  }

  private resetDeleteLaneModal(): void {
    this.laneToDelete = null;
    this.deleteLaneContext = null;
  }

  onConfirmDeleteLane(lane: LaneTableRow): void {
    const context = this.deleteLaneContext;
    if (!context || this.isDeletingLane) {
      return;
    }

    this.isDeletingLane = true;
    this.laneService.deleteLane(context.companyId, context.locationId, lane.id).subscribe({
      next: response => {
        this.isDeletingLane = false;
        if (response.status) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Lane deleted successfully',
          });
          this.resetDeleteLaneModal();
          // Only refresh the visible table if the admin is still viewing the
          // location the deleted lane belonged to.
          if (this.companyId === context.companyId && this.locationId === context.locationId) {
            this.loadLanes();
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to delete lane',
          });
        }
      },
      error: err => {
        this.isDeletingLane = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to delete lane',
        });
      },
    });
  }
}
